
import Vue from 'vue';
import debounce from 'lodash/debounce';
import TemplateCard from '@/views/pages/dynamicList/TemplateCard.vue';
import ListCreateDialog from '@/views/pages/dynamicList/ListCreateDialog.vue';
import { store } from '@/store/index';
import {
  dispatchGetDynamicListTemplates,
  dispatchGetDynamicListCategories,
} from '@/store/main/actionsMain';
import { readAssertedCompany } from '@/store/main/getters';
import { commitAddNotification } from '@/store/main/mutations';
import { usePagination } from '@/composables/usePagination';
import { useCompanyFeatureComposable } from '@/composables/useCompanyFeatures';
import { usePermissionComposable } from '@/composables/usePermission';
import { defineMessages } from '@/lib/i18n';
import sharedMessages from '@/lib/shared-messages';
import { COLORS } from './templateUtils';

const m = defineMessages({
  all: 'すべて',
  description: 'お探しのチェックリストが見つかりませんか?',
  errorLoadCategories: 'カテゴリの読み込みに失敗しました',
  errorLoadTemplates: 'チェックリスト一覧の読み込みに失敗しました',
  freePlanNotice:
    'ケミカンでは、お客様のご要望をもとに、チェックリスト機能の追加を検討いたします。ぜひお気軽にお知らせください。',
  loadingTemplates: 'チェックリスト一覧を読み込み中...',
  noTemplatesFound: 'チェックリストが見つかりませんでした',
  searchPlaceholder: '法令・規則名・業務内容などから検索できます',
  title: 'チェックリスト一覧に戻る',
  successCreated: 'リストを作成しました',
  consultationSent: 'チェックリストの追加の相談が送信されました',
  paginationInfo: '{current} / {total} (全{count}件中)',
});

interface Category {
  id: number;
  name: string;
  description?: string;
  display_order: number;
}

interface Template {
  id: number;
  name: string;
  overview?: string;
  template_type: 'free_default' | 'free' | 'paid';
  country_code: string;
  dbt_types: string[];
  display_order: number;
  categories: Category[];
  labels: Array<{ id: number; name: string }>;
  laws: Array<{ id: number; name: string }>;
  regulations: Array<{ id: number; name: string }>;
  related_links?: Array<{ id: number; url: string; title: string }>;
  has_access?: boolean;
}

export default Vue.extend({
  name: 'DynamicListTemplatesPage',
  components: {
    TemplateCard,
    ListCreateDialog,
  },
  setup() {
    const pagination = usePagination({ initialPage: 1, pageSize: 30 });
    const { isFeatureActive, COMPANY_FEATURES, generateInactiveMessage } =
      useCompanyFeatureComposable();
    const { hasPermission, PERMISSIONS: P } = usePermissionComposable();
    return {
      m,
      sharedMessages,
      pagination,
      isFeatureActive,
      COMPANY_FEATURES,
      generateInactiveMessage,
      hasPermission,
      P,
      COLORS,
    };
  },
  data() {
    return {
      loading: true,
      initialLoad: true,
      templates: [] as Template[],
      categories: [] as Category[],
      searchQuery: '',
      selectedCategoryId: null as number | null,
      isListCreateDialogVisible: false,
      dialogInitialMode: '',
      debouncedSearch: null as ReturnType<typeof debounce> | null,
    };
  },
  computed: {
    currentPage(): number {
      return this.pagination.currentPage.value;
    },
    pageSize(): number {
      return this.pagination.pageSize;
    },
    totalPages(): number {
      return this.pagination.totalPages.value;
    },
    totalTemplates(): number {
      return this.pagination.totalItems.value;
    },
    company() {
      return readAssertedCompany(store);
    },
    sortedCategories(): Category[] {
      return [...this.categories].sort(
        (a, b) => a.display_order - b.display_order
      );
    },
    showEmptyState(): boolean {
      return !this.loading && !this.initialLoad && this.templates.length === 0;
    },
    paginationInfoText(): string {
      return this.$t(m.paginationInfo, {
        current: String(this.currentPage),
        total: String(this.totalPages),
        count: String(this.totalTemplates),
      }) as string;
    },
  },
  created() {
    this.debouncedSearch = debounce(() => {
      this.pagination.reset();
      this.loadTemplates();
    }, 300);
  },
  async mounted() {
    try {
      await this.loadCategories();
      await this.loadTemplates();
    } catch (error) {
      // Error notification is handled by loadCategories/loadTemplates
    }
  },
  methods: {
    getFilterChipClass(categoryId: number | null): string {
      return this.selectedCategoryId === categoryId
        ? 'filter-chip-selected'
        : 'filter-chip-default';
    },
    async loadCategories() {
      try {
        const response = await dispatchGetDynamicListCategories(store);
        if (response?.data) {
          this.categories = response.data;
        }
      } catch (error) {
        commitAddNotification(store, {
          messageKey: m.errorLoadCategories,
          color: 'error',
        });
      }
    },
    async loadTemplates() {
      this.loading = true;
      try {
        const params: Record<string, string | number> = {
          page: this.currentPage,
          limit: this.pageSize,
        };

        if (this.selectedCategoryId !== null) {
          params.category_id = this.selectedCategoryId;
        }

        if (this.searchQuery?.trim()) {
          params.search = this.searchQuery.trim();
        }

        const response = await dispatchGetDynamicListTemplates(store, params);
        if (response?.data) {
          this.templates = response.data.templates || response.data;
          this.pagination.setTotal(
            response.data.total || this.templates.length
          );
        }
      } catch (error) {
        commitAddNotification(store, {
          messageKey: m.errorLoadTemplates,
          color: 'error',
        });
      } finally {
        this.loading = false;
        this.initialLoad = false;
      }
    },
    selectCategory(categoryId: number | null) {
      this.selectedCategoryId = categoryId;
      this.pagination.reset();
      this.loadTemplates();
    },
    handleTemplateSelect(template: Template) {
      // Navigate to template detail page
      this.$router.push({
        name: 'sdsDynamicListTemplateDetail',
        params: { templateId: String(template.id) },
      });
    },
    goBack() {
      this.$router.push({ name: 'sdsDynamicList' });
    },
    contactChemican() {
      this.dialogInitialMode = 'byRequests';
      this.isListCreateDialogVisible = true;
    },
    handleListCreated(isConsultation?: boolean) {
      this.isListCreateDialogVisible = false;
      commitAddNotification(store, {
        messageKey: isConsultation ? m.consultationSent : m.successCreated,
        color: 'success',
      });
    },
    previousPage() {
      this.pagination.previousPage();
      this.loadTemplates();
    },
    nextPage() {
      this.pagination.nextPage();
      this.loadTemplates();
    },
  },
});
